import React from 'react'
import { get } from 'lodash'
import { Text } from '@hub/text'
import { Link } from '@hub/link'
import { Button } from '@hub/button'
import UtilityBox from '../utility-box-v2'
import UtilityBoxItem, { UtilityBoxItemIcons } from '../utility-box-item-v2'
import UtilityBoxOpeningHours from '../utility-box-opening-hours'
import { parseServiceHours } from '../../helpers/parse-opening-hours'
import { conciergeTitle } from '../../helpers/concierge'
import locationLink from '../../helpers/location-link'
import {
  CentreTradingHours,
  StoreTradingSchedule,
  ParsedOpeningHour,
} from '../../helpers/parse-opening-hours/types'
import { Centre } from '../../../../apps/website/lib/get-centre-by-slug'

interface ServiceUtilityBox {
  centre: Centre
  centreTradingHours: CentreTradingHours
  storeTradingHours?: StoreTradingSchedule
}

const openingHours = (
  centreTradingHours: CentreTradingHours,
  storeTradingHours?: StoreTradingSchedule
): ParsedOpeningHour[] => {
  if (storeTradingHours) {
    return parseServiceHours(
      get(storeTradingHours, ['tradingHours']) || [],
      centreTradingHours.timeZone
    )
  }
  return parseServiceHours(
    get(centreTradingHours, ['tradingHours']) || [],
    centreTradingHours.timeZone
  )
}

const ServiceUtilityBox: React.FC<
  React.PropsWithChildren<ServiceUtilityBox>
> = ({ centre, centreTradingHours, storeTradingHours }): JSX.Element | null => {
  const address = centre.physicalAddress
  const address2 = `${centre.suburb} ${centre.state || ''} ${centre.postcode}`

  const externalLocationLink = locationLink(centre.latitude, centre.longitude)

  return (
    <UtilityBox>
      <Text
        tone="strong"
        fontSize="font-2xl"
        color="textPrimary"
        sx={{
          marginBottom: 'spacing-xl',
        }}
      >{`
          ${centre.title} ${conciergeTitle(
            centre.country,
            centre.slug
          )}`}</Text>
      <UtilityBoxOpeningHours
        openingHours={openingHours(centreTradingHours, storeTradingHours)}
        centreSlug={centre.slug}
        unlinkedHours={Boolean(storeTradingHours)}
      />
      <UtilityBoxItem
        icon={UtilityBoxItemIcons.PHONE}
        role="heading"
        aria-label="Phone Number"
      >
        <Text>{centre.telephoneNumber}</Text>
      </UtilityBoxItem>
      {address && (
        <UtilityBoxItem
          icon={UtilityBoxItemIcons.LOCATION_DROP_PIN}
          key="item"
          role="heading"
          aria-label="Store Location"
        >
          <Text sx={{ paddingBottom: 'spacing-md' }}>{address}</Text>
          {address2 && (
            <Text sx={{ paddingBottom: 'spacing-md' }}>{address2}</Text>
          )}
        </UtilityBoxItem>
      )}
      {externalLocationLink && (
        <Button
          variant="link"
          as={Link}
          href={externalLocationLink}
          target="_blank"
          sx={{
            textDecoration: 'underline',
            fontWeight: 'bold',
            padding: 'spacing-none',
            height: 'size-8',
            textTransform: 'none',
            color: 'textPrimary',
            _hover: {
              color: 'textSecondary',
            },
          }}
        >
          See on map
        </Button>
      )}
    </UtilityBox>
  )
}

export default ServiceUtilityBox
